'use client';

import '@/styles/marketing.css';
import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { DocsHeader } from '@/components/docs/docs-header';
import { DocsMobileSectionNav } from '@/components/docs/docs-mobile-section-nav';
import { DocsScrollProvider, useDocsScroll } from '@/components/docs/docs-scroll-context';
import { DocsSidebar } from '@/components/docs/docs-sidebar';
import { DocsToc } from '@/components/docs/docs-toc';
import { DocsPanel } from '@/components/docs/docs-ui';
import { HudFrame } from '@/components/marketing/site/hud';

function DocsLayoutBody({ mobileOpen, setMobileOpen }: { mobileOpen: boolean; setMobileOpen: (open: boolean) => void }) {
  const { registerScrollRoot } = useDocsScroll();

  return (
    <div className="docs-layout__body flex min-h-0 flex-1 flex-col">
      <DocsMobileSectionNav />

      <div className="docs-layout shell relative z-10 min-h-0 flex-1 py-4 sm:py-5 lg:py-0">
        <div className="docs-layout__grid h-full min-h-0">
          <aside className="docs-layout__sidebar hidden min-h-0 lg:block">
            <div className="docs-layout__sidebar-sticky h-full">
              <DocsSidebar />
            </div>
          </aside>

          {mobileOpen ? (
            <>
              <button
                type="button"
                className="fixed inset-0 z-40 bg-black/60 lg:hidden"
                aria-label="Close docs navigation"
                onClick={() => setMobileOpen(false)}
              />
              <aside className="docs-layout__drawer border-line bg-ink/95 fixed inset-y-0 left-0 z-50 w-[min(100%,17rem)] overflow-y-auto border-r p-3 pt-20 backdrop-blur-xl lg:hidden">
                <DocsSidebar onNavigate={() => setMobileOpen(false)} />
              </aside>
            </>
          ) : null}

          <main
            ref={registerScrollRoot}
            className="docs-layout__main docs-layout__scroll min-h-0 min-w-0"
          >
            <DocsPanel className="docs-layout__panel p-5 sm:p-6 lg:p-7">
              <Outlet />
            </DocsPanel>
          </main>

          <aside className="docs-layout__toc hidden min-h-0 xl:block">
            <div className="docs-layout__toc-sticky h-full">
              <DocsToc />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

export function DocsLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const sync = () => {
      if (!media.matches) {
        if (!mobileOpen) document.body.style.overflow = '';
        return;
      }
      document.body.style.overflow = 'hidden';
    };
    sync();
    media.addEventListener('change', sync);
    return () => {
      media.removeEventListener('change', sync);
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <div className="marketing-site docs-site relative flex min-h-screen flex-col overflow-x-hidden lg:h-svh lg:overflow-hidden">
      <div aria-hidden className="grid-bg pointer-events-none fixed inset-0 opacity-[0.1]" />
      <HudFrame />
      <DocsHeader onOpenNav={() => setMobileOpen(true)} />
      <DocsScrollProvider>
        <DocsLayoutBody mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      </DocsScrollProvider>
    </div>
  );
}
